import { type ComponentType } from "react";
import { useSearchParams } from "react-router-dom";
import {
	Settings,
	Users,
	Lock,
	FileClock,
	Tags,
	ListTree,
	Trash2,
	GitBranch,
	Workflow,
	Plug,
	Mail,
	type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { GeneralSection } from "@/components/workspace-settings/GeneralSection";
import { MembersSection } from "@/components/workspace-settings/MembersSection";
import { RolePermissionsSection } from "@/components/workspace-settings/RolePermissionsSection";
import { SystemLogsSection } from "@/components/workspace-settings/SystemLogsSection";
import { TaskTypesSection } from "@/components/workspace-settings/TaskTypesSection";
import { TaskSubStatusesSection } from "@/components/workspace-settings/TaskSubStatusesSection";
import { WorkflowStagesSection } from "@/components/workspace-settings/WorkflowStagesSection";
import { TrashSection } from "@/components/workspace-settings/TrashSection";
import { GithubIntegrationSection } from "@/components/workspace-settings/GithubIntegrationSection";
import { ExternalAppIntegrationsSection } from "@/components/workspace-settings/ExternalAppIntegrationsSection";
import { EmailToTaskSection } from "@/components/workspace-settings/EmailToTaskSection";

interface SettingsNavItem {
	id: string;
	label: string;
	icon: LucideIcon;
	danger?: boolean;
}

const navItems: SettingsNavItem[] = [
	{ id: "general", label: "General", icon: Settings },
	{ id: "role-permissions", label: "Role Permissions", icon: Lock },
	{ id: "members", label: "Members & Roles", icon: Users },
	{ id: "task-types", label: "Task Types", icon: Tags },
	{ id: "task-sub-statuses", label: "Task Sub-Status", icon: ListTree },
	{ id: "workflow-stages", label: "Workflow Stages", icon: Workflow },
	{ id: "github-integration", label: "GitHub Integration", icon: GitBranch },
	{ id: "external-apps", label: "External App Integrations", icon: Plug },
	{ id: "email-to-task", label: "Email-to-Task", icon: Mail },
	{ id: "system-logs", label: "System Logs", icon: FileClock },
	{ id: "trash", label: "Trash", icon: Trash2 },
	// { id: "boards", label: "Boards & Workflow", icon: LayoutDashboard },
	// { id: "tickets", label: "Tickets", icon: Ticket },
	// { id: "notifications", label: "Notifications", icon: Bell },
	// { id: "integrations", label: "Integrations", icon: Plug },
	// { id: "security", label: "Security", icon: Shield },
	// { id: "danger", label: "Danger Zone", icon: AlertOctagon, danger: true },
];

const sections: Record<string, ComponentType> = {
	general: GeneralSection,
	"role-permissions": RolePermissionsSection,
	members: MembersSection,
	"task-types": TaskTypesSection,
	"task-sub-statuses": TaskSubStatusesSection,
	"workflow-stages": WorkflowStagesSection,
	"github-integration": GithubIntegrationSection,
	"external-apps": ExternalAppIntegrationsSection,
	"email-to-task": EmailToTaskSection,
	"system-logs": SystemLogsSection,
	trash: TrashSection,
};

export default function SettingsPage() {
	const [searchParams, setSearchParams] = useSearchParams();
	const tabParam = searchParams.get("tab") ?? "";
	const activeSection = tabParam in sections ? tabParam : "general";

	function selectSection(id: string) {
		setSearchParams({ tab: id }, { replace: true });
	}

	const ActiveSection = sections[activeSection] ?? GeneralSection;

	return (
		<div className="mx-auto max-w-[1280px] px-4 py-8 sm:px-6">
			<div className="mb-8">
				<h1 className="text-2xl font-semibold text-foreground">
					Workspace Settings
				</h1>
				<p className="text-sm text-muted mt-0.5">
					Manage your workspace's identity, team, and security
					preferences.
				</p>
			</div>

			{/* Mobile/tablet nav strip */}
			<div className="mb-6 overflow-x-auto lg:hidden">
				<div className="flex gap-1 border-b border-border pb-2">
					{navItems.map(({ id, label, icon: Icon, danger }) => (
						<button
							key={id}
							onClick={() => selectSection(id)}
							className={cn(
								"flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors",
								activeSection === id
									? danger
										? "bg-danger-subtle text-danger font-medium"
										: "bg-primary-subtle text-primary font-medium"
									: danger
										? "text-danger hover:bg-danger-subtle"
										: "text-muted-foreground hover:bg-muted-subtle hover:text-foreground",
							)}
						>
							<Icon className="h-3.5 w-3.5 shrink-0" />
							{label}
						</button>
					))}
				</div>
			</div>

			<div className="flex gap-8 items-start">
				{/* Sidebar — desktop only */}
				<nav className="hidden w-52 shrink-0 sticky top-22 lg:block">
					<ul className="space-y-0.5">
						{navItems.map(({ id, label, icon: Icon, danger }) => (
							<li key={id}>
								<button
									onClick={() => selectSection(id)}
									className={cn(
										"w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors text-left",
										activeSection === id
											? danger
												? "bg-danger-subtle text-danger font-medium"
												: "bg-primary-subtle text-primary font-medium"
											: danger
												? "text-danger hover:bg-danger-subtle"
												: "text-muted-foreground hover:bg-muted-subtle hover:text-foreground",
									)}
								>
									<Icon className="h-4 w-4 shrink-0" />
									{label}
								</button>
							</li>
						))}
					</ul>
				</nav>

				{/* Content */}
				<div className="flex-1 space-y-6 min-w-0">
					<ActiveSection />
				</div>
			</div>
		</div>
	);
}
